/**
 * What a tool's answer looks like, so its row can draw it instead of dumping JSON: a list of
 * records becomes a table (sessions, machines, pipelines), one record becomes a key and value
 * card, a refusal becomes the reason, prose stays prose. Pure, so it is tested
 * (__tests__/shape.test.ts).
 */

export type ResultShape =
  | { kind: 'rows'; columns: string[]; rows: Record<string, string>[]; numeric: boolean[]; total: number; meta: [string, string][] }
  | { kind: 'record'; fields: [string, string][] }
  | { kind: 'text'; text: string }
  | { kind: 'failure'; message: string }
  | { kind: 'empty' };

/** At most this many columns and rows are drawn; the rest stays in the raw answer. */
export const MAX_COLUMNS = 6;
export const MAX_ROWS = 200;
const MAX_FIELDS = 24;

/** Columns people look for first, in this order. */
const PREFERRED = ['name', 'title', 'label', 'status', 'state', 'liveness', 'project', 'projectName', 'machine', 'machineName', 'kind', 'branch', 'model', 'cost', 'costUsd', 'lastActivity', 'updatedAt', 'lastSeen', 'startedAt', 'at', 'id'];
/** Fields that say nothing to a person. */
const NOISE = /^(_|self$|href$|links?$|etag$|cursor$|nextCursor$|token$|signature$|raw$|modules$|pricingVersion$|[a-z]+Total$|.*\.?currency$)/i;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** A key in words: lastActivityAt → Last activity at; cost_usd → Cost usd. */
export function keyWords(k: string): string {
  const w = k
    // Money is shown as money: the unit in the name says nothing more.
    .replace(/(Usd|_usd|USD)$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .toLowerCase();
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : k;
}

/** Keys whose numbers are moments: at, startedAt, lastActivity, lastSeen, created_at. */
const TIME_KEY = /(^at$|At$|_at$|Ts$|^ts$|time$|Time$|date$|Date$|^lastActivity$|^lastSeen$)/;

/** One value as text: times as dates, booleans as Yes and No, lists joined, numbers as they are. */
/** Keys whose numbers are US dollars: any part of the path names cost, spend, price, budget or USD. */
export const MONEY_KEY = /(^|[._])(cost|spend|price|budget|usd)|(Cost|Usd|Spend|Price|Budget)($|[._])/;

export interface ValueFormat {
  date: (ts: number) => string;
  number: (n: number) => string;
  money?: (usd: number) => string;
}

export function valueText(key: string, v: unknown, fmt: ValueFormat): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') {
    // Epoch milliseconds or seconds under a time-like key.
    if (TIME_KEY.test(key) && v > 1e12 && v < 1e14) return fmt.date(v);
    if (TIME_KEY.test(key) && v > 1e9 && v < 1e10) return fmt.date(v * 1000);
    if (fmt.money && MONEY_KEY.test(key)) return fmt.money(v);
    return fmt.number(v);
  }
  if (typeof v === 'string') {
    if (TIME_KEY.test(key) && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const t = Date.parse(v);
      if (Number.isFinite(t)) return fmt.date(t);
    }
    return v;
  }
  if (Array.isArray(v)) return v.every((x) => typeof x !== 'object' || x === null) ? v.slice(0, 10).join(', ') + (v.length > 10 ? `, and ${v.length - 10} more` : '') : `${v.length} items`;
  if (isObj(v)) {
    const name = v.name ?? v.title ?? v.label ?? v.id;
    return typeof name === 'string' || typeof name === 'number' ? String(name) : `${Object.keys(v).length} fields`;
  }
  return String(v);
}

/** An object one level flat: { a: { b: 1 } } → { 'a.b': 1 }; deeper objects stay whole. */
function flatten(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (NOISE.test(k)) continue;
    if (isObj(v) && Object.keys(v).length <= 6 && Object.values(v).every((x) => !isObj(x) && !Array.isArray(x))) {
      for (const [k2, v2] of Object.entries(v)) if (!NOISE.test(k2)) out[`${k}.${k2}`] = v2;
    } else out[k] = v;
  }
  return out;
}

/** A refusal or error inside an answer: { ok: false, error } or { error: { what, why } }. */
function failureOf(v: Record<string, unknown>): string | null {
  const err = v.error;
  if (isObj(err) && typeof err.what === 'string') return [err.what, err.why, err.next].filter((x) => typeof x === 'string' && x).join(' ');
  if (v.ok === false || v.success === false || v.isError === true) {
    const m = typeof err === 'string' ? err : typeof v.message === 'string' ? v.message : null;
    return m ?? 'It reported a failure without saying why.';
  }
  if (typeof err === 'string' && err && Object.keys(v).length <= 4) return err;
  return null;
}

function rowsOf(list: Record<string, unknown>[], fmt: ValueFormat): Pick<Extract<ResultShape, { kind: 'rows' }>, 'columns' | 'rows' | 'numeric'> {
  const flat = list.slice(0, MAX_ROWS).map(flatten);
  const count = new Map<string, number>();
  for (const r of flat) for (const [k, v] of Object.entries(r)) if (v !== null && v !== undefined && v !== '' && !isObj(v)) count.set(k, (count.get(k) ?? 0) + 1);
  // Keep columns filled in at least half the rows, people's favourites first.
  const keys = [...count.entries()].filter(([, n]) => n >= flat.length / 2).map(([k]) => k);
  const rank = (k: string) => {
    const i = PREFERRED.indexOf(k);
    return i < 0 ? PREFERRED.length : i;
  };
  const columns = keys.sort((a, b) => rank(a) - rank(b)).slice(0, MAX_COLUMNS);
  const numeric = columns.map((c) => flat.every((r) => r[c] === undefined || r[c] === null || (typeof r[c] === 'number' && !TIME_KEY.test(c))));
  return { columns, numeric, rows: flat.map((r) => Object.fromEntries(columns.map((c) => [c, valueText(c, r[c], fmt)]))) };
}

/** The shape of a tool's answer (its text as the model read it). */
export function resultShape(text: string | undefined, fmt: ValueFormat): ResultShape {
  if (text === undefined || !text.trim()) return { kind: 'empty' };
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return { kind: 'text', text };
  }
  // An MCP content list: its text parts, read again.
  if (isObj(v) && Array.isArray(v.content) && v.content.every((p) => isObj(p) && p.type === 'text')) {
    return resultShape((v.content as { text: string }[]).map((p) => p.text).join('\n'), fmt);
  }
  if (Array.isArray(v)) {
    if (!v.length) return { kind: 'empty' };
    const objs = v.filter(isObj);
    if (objs.length === v.length) return { kind: 'rows', ...rowsOf(objs, fmt), total: v.length, meta: [] };
    return { kind: 'text', text: v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join('\n') };
  }
  if (!isObj(v)) return v === null ? { kind: 'empty' } : { kind: 'text', text: String(v) };
  const fail = failureOf(v);
  if (fail) return { kind: 'failure', message: fail };
  // { data: ... } or { result: ... } beside bookkeeping only: look inside.
  const keys = Object.keys(v).filter((k) => !['ok', 'status', 'auditId', 'truncated', 'message'].includes(k));
  if (keys.length === 1 && (isObj(v[keys[0]!]) || Array.isArray(v[keys[0]!]))) {
    const inner = resultShape(JSON.stringify(v[keys[0]!]), fmt);
    if (inner.kind !== 'text') return inner;
  }
  // One list of records beside a few facts: the list is the table, the facts its caption.
  const lists = Object.entries(v).filter(([, x]) => Array.isArray(x) && x.length > 0 && x.every(isObj));
  if (lists.length === 1) {
    const [key, list] = lists[0]! as [string, Record<string, unknown>[]];
    const meta = Object.entries(v)
      .filter(([k, x]) => k !== key && !isObj(x) && !Array.isArray(x) && !NOISE.test(k))
      .slice(0, 4)
      .map(([k, x]) => [keyWords(k), valueText(k, x, fmt)] as [string, string])
      .filter(([, x]) => x);
    return { kind: 'rows', ...rowsOf(list, fmt), total: list.length, meta };
  }
  const fields = Object.entries(flatten(v))
    .map(([k, x]) => [keyWords(k), valueText(k, x, fmt)] as [string, string])
    .filter(([, x]) => x !== '');
  if (!fields.length) return { kind: 'empty' };
  return { kind: 'record', fields: fields.slice(0, MAX_FIELDS) };
}
